import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRole } from "@/contexts/RoleContext";
import { getFmLearnerState } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Layers, Lock, CheckCircle2, Loader2, Minus, PlayCircle } from "lucide-react";

const SKILLS = [
  { key: "Addition",       symbol: "+" },
  { key: "Subtraction",    symbol: "−" },
  { key: "Multiplication", symbol: "×" },
  { key: "Division",       symbol: "÷" },
];

// Tappable = can navigate to session
function isTappable(skillData) {
  const s = skillData?.state;
  if (s === "in_progress") return true;
  if (s === "not_started" && skillData?.teacher_unlocked) return true;
  return false;
}

const STATE_CONFIG = {
  not_started_locked: {
    label: "Not Started",
    sub: "Complete the previous skill first",
    badge: "bg-slate-100 text-slate-500",
    card: "bg-white border border-slate-200",
    icon: <Lock className="w-5 h-5 text-slate-300" />,
    symbolColor: "text-slate-300",
    cursor: "cursor-default opacity-70",
  },
  not_started_unlocked: {
    label: "Ready to Start",
    sub: "Tap to begin the gate check",
    badge: "bg-emerald-100 text-emerald-700",
    card: "bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200",
    icon: <PlayCircle className="w-5 h-5 text-emerald-500" />,
    symbolColor: "text-emerald-200",
    cursor: "cursor-pointer hover:-translate-y-1 transition-transform",
  },
  locked: {
    label: "Locked",
    sub: "Teacher unlock required",
    badge: "bg-red-100 text-red-600",
    card: "bg-red-50 border border-red-200",
    icon: <Lock className="w-5 h-5 text-red-400" />,
    symbolColor: "text-red-200",
    cursor: "cursor-default opacity-70",
  },
  in_progress: {
    label: "In Progress",
    sub: null,
    badge: "bg-fuchsia-100 text-fuchsia-700",
    card: "bg-gradient-to-br from-fuchsia-50 to-fuchsia-100 border border-fuchsia-200",
    icon: null,
    symbolColor: "text-fuchsia-200",
    cursor: "cursor-pointer hover:-translate-y-1 transition-transform",
  },
  mastered: {
    label: "Mastered",
    sub: "Well done!",
    badge: "bg-amber-100 text-amber-700",
    card: "bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200",
    icon: <CheckCircle2 className="w-5 h-5 text-amber-500" />,
    symbolColor: "text-amber-200",
    cursor: "cursor-default",
  },
};

function getConfig(skillData) {
  const s = skillData?.state || "not_started";
  if (s === "not_started") {
    return skillData?.teacher_unlocked ? STATE_CONFIG.not_started_unlocked : STATE_CONFIG.not_started_locked;
  }
  return STATE_CONFIG[s] || STATE_CONFIG.not_started_locked;
}

export default function MathFoundations() {
  const { learner } = useRole();
  const nav = useNavigate();
  const [skills, setSkills] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!learner?.learner_id) return;
    getFmLearnerState(learner.learner_id)
      .then(data => setSkills(data.skills))
      .catch(() => setSkills({}))
      .finally(() => setLoading(false));
  }, [learner]);

  const handleCardClick = (skillKey, skillData) => {
    if (!isTappable(skillData)) return;
    nav(`/fm/${encodeURIComponent(skillKey)}`);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10" data-testid="math-foundations-page">
      {/* Back */}
      <Link
        to="/home"
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-fuchsia-600 transition-colors mb-8"
        data-testid="fm-back-link"
      >
        <ArrowLeft className="w-4 h-4" /> Back to home
      </Link>

      {/* Header */}
      <div className="flex items-center gap-4 mb-2">
        <div className="w-14 h-14 rounded-2xl bg-fuchsia-100 text-fuchsia-700 flex items-center justify-center flex-shrink-0">
          <Layers className="w-7 h-7" />
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-800" data-testid="fm-title">
            Math Foundations
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Number Skills · All Grades</p>
        </div>
      </div>

      <p className="text-slate-500 text-sm mb-8 ml-[4.5rem]">
        Build strong number sense — one skill at a time.
      </p>

      {/* Skill cards */}
      {loading ? (
        <div className="flex justify-center py-16" data-testid="fm-loading">
          <Loader2 className="w-8 h-8 text-fuchsia-400 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="fm-skill-grid">
          {SKILLS.map(({ key, symbol }) => {
            const skillData = skills?.[key] || { state: "not_started", teacher_unlocked: false, current_level: null };
            const cfg = getConfig(skillData);
            const tappable = isTappable(skillData);

            return (
              <Card
                key={key}
                onClick={() => handleCardClick(key, skillData)}
                data-testid={`fm-skill-card-${key.toLowerCase()}`}
                className={`rounded-3xl p-7 border ${cfg.card} relative overflow-hidden ${cfg.cursor} select-none`}
              >
                {/* Operator watermark */}
                <span
                  className={`absolute right-5 bottom-3 font-display text-7xl font-black ${cfg.symbolColor} pointer-events-none leading-none`}
                  aria-hidden="true"
                >
                  {symbol}
                </span>

                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="font-display text-xl font-bold text-slate-800">{key}</h2>
                    {skillData.state === "in_progress" && skillData.current_level ? (
                      <span
                        className="text-xs font-bold px-2.5 py-1 rounded-full bg-fuchsia-600 text-white"
                        data-testid={`fm-skill-level-${key.toLowerCase()}`}
                      >
                        {skillData.current_level}
                      </span>
                    ) : (
                      cfg.icon
                    )}
                  </div>

                  <span
                    className={`inline-block text-xs font-bold px-2.5 py-1 rounded-full ${cfg.badge}`}
                    data-testid={`fm-skill-state-${key.toLowerCase()}`}
                  >
                    {cfg.label}
                  </span>

                  {skillData.state === "in_progress" && skillData.current_level && (
                    <p className="text-xs text-slate-500 mt-2">Currently at {skillData.current_level}</p>
                  )}
                  {skillData.state !== "in_progress" && cfg.sub && (
                    <p className="text-xs text-slate-500 mt-2">{cfg.sub}</p>
                  )}

                  {tappable && (
                    <p className="text-xs font-semibold text-fuchsia-600 mt-3">Tap to {skillData.state === "in_progress" ? "continue" : "begin"} →</p>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
